// Die Züge der Kolonie (Akt III) — Kodierung und Solo-Fassung.
//
// Ein Zug läuft durch dieselbe Weiche wie jeder andere (`net/moves.ts`):
// `(kind, id, times)`. Die ID trägt hier mehrere Felder, getrennt durch `:` —
// die Kodierung steht **einmal**, hier, für Panel, Szene und beide Wege.
// Zum Server gehen sie als einzelne Felder (`ColonyMove`), nicht als Zeichenkette.
//
// Die Solo-Fassung spiegelt `backend/farmining/progression/colony.py`: jeder Zug
// schreibt erst die Zeit fort und prüft dann. Ressourcen sinken über
// `store.pay`, Güter über `payGoods` — die beiden einzigen abziehenden Pfade.

import { balancing, colonyBuildingById, planetById } from '../data/balancing';
import type { StatMap } from '../data/types';
import {
  advance, buildingAt, buildingCost, canAfford, inArea, landLocked, nextProject, payGoods,
  placeCost, placeLocked, siteOk, splitCost, type ColonyBuildingState,
} from '../sim/colony';
import { center } from '../sim/planets';
import { nowSeconds, store } from '../state/store';
import type { ColonyMove, ColonyOp } from './api';

export type ColonyKind = 'land' | 'place' | 'upgrade' | 'recipe' | 'switch' | 'relocate' | 'project';

export interface ColonyResult {
  ok: boolean;
  message?: string;
}

const ABGELEHNT: ColonyResult = { ok: false };

// ── Kodierung ────────────────────────────────────────────────────────────────

/** Die IDs der Kolonie-Züge — **eine** Stelle, an der sie gebaut werden. */
export const colonyId = {
  planet: (planet: string): string => planet,
  place: (planet: string, kind: string, x: number, y: number): string => `${planet}:${kind}:${x}:${y}`,
  tile: (planet: string, x: number, y: number): string => `${planet}:${x}:${y}`,
  recipe: (planet: string, x: number, y: number, recipe: string): string =>
    `${planet}:${x}:${y}:${recipe}`,
  switch: (planet: string, x: number, y: number, on: boolean): string =>
    `${planet}:${x}:${y}:${on ? 'on' : 'off'}`,
  relocate: (planet: string, x: number, y: number, tx: number, ty: number): string =>
    `${planet}:${x}:${y}:${tx}:${ty}`,
};

const ganz = (text: string | undefined): number | null =>
  text !== undefined && /^\d+$/.test(text) ? Number(text) : null;

/**
 * Eine Zug-ID zerlegen — oder `null`, wenn sie nicht passt.
 *
 * Ein kaputter Zug wird abgelehnt, bevor er irgendwo hingeht. Ob er nach den
 * Regeln erlaubt ist, entscheidet danach die Regel, nicht diese Funktion.
 */
export function colonyArgs(kind: ColonyKind, id: string, times = 1): ColonyMove | null {
  const teile = id.split(':');
  const planet = teile[0];
  if (!planetById(planet) || teile.length !== FELDER[kind]) return null;
  if (kind === 'land' || kind === 'project') return { planet };
  if (kind === 'place') return kachel(planet, teile[2], teile[3], { kind: teile[1] });
  const ort = kachel(planet, teile[1], teile[2], {});
  if (!ort) return null;
  if (kind === 'upgrade') return { ...ort, times };
  if (kind === 'recipe') return { ...ort, recipe: teile[3] };
  if (kind === 'switch') {
    return teile[3] === 'on' || teile[3] === 'off' ? { ...ort, on: teile[3] === 'on' } : null;
  }
  const [tx, ty] = [ganz(teile[3]), ganz(teile[4])];
  return tx !== null && ty !== null ? { ...ort, tx, ty } : null;
}

/** Wie viele Felder die ID einer Zugart trägt — samt Planet. */
const FELDER: Record<ColonyKind, number> = {
  land: 1, project: 1, place: 4, upgrade: 3, recipe: 4, switch: 4, relocate: 5,
};

/** Planet und Kachel — oder `null`, wenn eine Koordinate keine ganze Zahl ist. */
function kachel(planet: string, xs: string, ys: string, extra: Partial<ColonyMove>): ColonyMove | null {
  const x = ganz(xs);
  const y = ganz(ys);
  return x !== null && y !== null ? { planet, ...extra, x, y } : null;
}

/** Welcher Endpunkt zu welcher Zugart gehört. */
export const COLONY_OPS: Record<ColonyKind, ColonyOp> = {
  land: 'land', place: 'place', upgrade: 'upgrade', recipe: 'recipe',
  switch: 'switch', relocate: 'relocate', project: 'project',
};

// ── Solo ─────────────────────────────────────────────────────────────────────

/** Gemischte Kosten bezahlen — erst prüfen, dann buchen. Alles oder nichts. */
function pay(cost: StatMap): boolean {
  if (!canAfford(store.save, cost)) return false;
  const geteilt = splitCost(cost);
  return store.pay(geteilt.ship) && payGoods(store.save.colony, geteilt.goods);
}

function anlage(move: ColonyMove): ColonyBuildingState | undefined {
  const outpost = store.save.colony.planets[move.planet];
  return outpost ? buildingAt(outpost, move.x ?? -1, move.y ?? -1) : undefined;
}

/** Die Solo-Fassung eines Kolonie-Zugs. Spiegelt `progression/colony.py`. */
export function applyLocalColonyMove(kind: ColonyKind, id: string, times = 1): ColonyResult {
  const move = colonyArgs(kind, id, times);
  if (!move) return ABGELEHNT;
  advance(store.save, nowSeconds());
  if (kind === 'land') return land(move);
  if (kind === 'place') return place(move);
  if (kind === 'project') return project(move);
  if (!store.save.colony.planets[move.planet]) return { ok: false, message: 'Hier ist noch niemand gelandet.' };
  const ziel = anlage(move);
  if (!ziel) return { ok: false, message: 'Auf dieser Kachel steht keine Anlage.' };
  if (kind === 'upgrade') return upgrade(ziel, move.times ?? 1);
  if (kind === 'recipe') return setRecipe(ziel, move.recipe ?? '');
  if (kind === 'switch') return setOn(ziel, move.on ?? true);
  return relocate(move, ziel);
}

function land(move: ColonyMove): ColonyResult {
  const planet = planetById(move.planet);
  if (!planet) return ABGELEHNT;
  const grund = landLocked(store.save, planet);
  if (grund) return { ok: false, message: grund };
  if (!pay(planet.landCost)) return { ok: false, message: 'Die Kosten sind nicht gedeckt.' };
  const mitte = center();
  const colony = store.save.colony;
  const neu = { ...colony.planets, [planet.id]: { buildings: [{ kind: 'pod', x: mitte.x, y: mitte.y, level: 1, recipe: '', on: true }], projects: 0 } };
  // Tabellenordnung halten — der Produktionsschritt summiert in dieser Folge.
  colony.planets = Object.fromEntries(
    balancing().planets.filter((entry) => entry.id in neu).map((entry) => [entry.id, neu[entry.id]]),
  );
  for (const [gut, menge] of Object.entries(planet.kit)) colony.goods[gut] = (colony.goods[gut] ?? 0) + menge;
  if (colony.t <= 0) colony.t = nowSeconds();
  store.refresh();
  return { ok: true };
}

function place(move: ColonyMove): ColonyResult {
  const kind = colonyBuildingById(move.kind ?? '');
  if (!kind) return ABGELEHNT;
  const x = move.x ?? -1;
  const y = move.y ?? -1;
  const grund = placeLocked(store.save, move.planet, kind, x, y);
  if (grund) return { ok: false, message: grund };
  if (!pay(placeCost(store.save.colony, move.planet, kind))) {
    return { ok: false, message: 'Die Kosten sind nicht gedeckt.' };
  }
  store.save.colony.planets[move.planet].buildings.push({
    kind: kind.id, x, y, level: 1, recipe: kind.recipes[0] ?? '', on: true,
  });
  store.count('colonyBuilt');
  return { ok: true };
}

/** Bis zu `times` Stufen — geklemmt auf das Gedeckte, abgelehnt erst bei null. */
function upgrade(ziel: ColonyBuildingState, times: number): ColonyResult {
  const kind = colonyBuildingById(ziel.kind);
  if (!kind) return ABGELEHNT;
  if (ziel.level >= kind.maxLevel) return { ok: false, message: `${kind.name} ist bereits voll ausgebaut.` };
  if (!pay(buildingCost(kind, ziel.level))) return { ok: false, message: 'Die Kosten sind nicht gedeckt.' };
  ziel.level++;
  store.count('colonyUpgrades');
  const grenze = Math.min(times, balancing().colony.maxUpgradeRuns);
  for (let i = 1; i < grenze && ziel.level < kind.maxLevel; i++) {
    if (!pay(buildingCost(kind, ziel.level))) break;
    ziel.level++;
    store.count('colonyUpgrades');
  }
  return { ok: true };
}

function setRecipe(ziel: ColonyBuildingState, recipe: string): ColonyResult {
  const kind = colonyBuildingById(ziel.kind);
  if (!kind || !kind.recipes.includes(recipe)) {
    return { ok: false, message: `${kind?.name ?? 'Die Anlage'} kennt dieses Rezept nicht.` };
  }
  ziel.recipe = recipe;
  return { ok: true };
}

function setOn(ziel: ColonyBuildingState, on: boolean): ColonyResult {
  if (ziel.kind === 'pod') return { ok: false, message: 'Die Kapsel lässt sich nicht abschalten.' };
  ziel.on = on;
  return { ok: true };
}

function relocate(move: ColonyMove, ziel: ColonyBuildingState): ColonyResult {
  const kind = colonyBuildingById(ziel.kind);
  if (!kind) return ABGELEHNT;
  if (kind.role === 'hq') return { ok: false, message: 'Die Kapsel bleibt, wo sie gelandet ist.' };
  const tx = move.tx ?? -1;
  const ty = move.ty ?? -1;
  if (tx === ziel.x && ty === ziel.y) return { ok: true };
  const outpost = store.save.colony.planets[move.planet];
  if (!inArea(store.save.colony, move.planet, tx, ty)) {
    return { ok: false, message: 'Diese Kachel liegt außerhalb des erschlossenen Gebiets.' };
  }
  if (buildingAt(outpost, tx, ty)) return { ok: false, message: 'Hier steht schon etwas.' };
  if (!siteOk(move.planet, kind, tx, ty)) return { ok: false, message: `${kind.name} passt nicht auf diese Kachel.` };
  ziel.x = tx;
  ziel.y = ty;
  return { ok: true };
}

function project(move: ColonyMove): ColonyResult {
  if (!store.save.colony.planets[move.planet]) return { ok: false, message: 'Hier ist noch niemand gelandet.' };
  const projekt = nextProject(store.save.colony, move.planet);
  if (!projekt) return { ok: false, message: 'Auf diesem Planeten ist alles gebaut.' };
  if (!pay(projekt.cost)) return { ok: false, message: 'Die Kosten sind nicht gedeckt.' };
  store.save.colony.planets[move.planet].projects++;
  store.count('colonyProjects');
  store.refresh();
  return { ok: true };
}
